import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { localizeRole, Role } from '../../model/auth/Role';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CompetitionService } from '../../service/competition.service';
import { ProgressService } from '../../service/progress.service';
import { BaseModalComponent } from '../../common/modal/base-modal/base-modal.component';
import { NotificationHandler } from '../../helper/notification.handler';
import { Observable } from 'rxjs';
import { InviteUserDto, User } from '../../model/auth/User';
import { UserService } from '../../service/user.service';
import { ALL_LOCALES, DEFAULT_LOCALE, Locale, localizeLocale } from '../../model/Locale';
import { School, SchoolBaseDto, SchoolGrade, SchoolType } from '../../model/location/School';
import { Region } from '../../model/location/Region';
import { SchoolSelectResult } from '../school-select/school-select.component';
import { ContestService } from '../../service/contest.service';
import { Contest } from '../../model/contest/Contest';
import { ContestEnrolResultData } from '../../contest/contest-enrol-modal/contest-enrol-modal.component';
import { EventCategory, EventType, TrackingService } from '../../service/tracking.service';
import { MultiActionItem } from '../../common/button/button.component';
import { ItemAutocompleteComponent } from '../../common/item-autocomplete/item-autocomplete.component';
import { SchoolDetailsData, SchoolDetailsModalComponent } from '../school-details-modal/school-details-modal.component';
import { TenantService } from '../../service/tenant.service';
import { Competition } from '../../model/competition/Competition';
import { ErrorInterceptor } from '../../helper/error.interceptor';

export interface InviteUserDetailsData {
  competitionId: string;
  contest?: Contest;

  requestedRole?: Role;

  grade?: SchoolGrade;
  region?: Region;
  school?: School;
  schoolType?: SchoolType;

  usernameMode?: boolean;
}

export interface InviteUserResult extends ContestEnrolResultData {
  competitionId: string;
  user?: User;

  addMore?: boolean;
  addMoreData?: InviteUserDetailsData;
}

@Component({
  selector: 'app-invite-user-modal',
  templateUrl: './invite-user-modal.component.html',
  styleUrls: ['./invite-user-modal.component.scss']
})
export class InviteUserModalComponent extends BaseModalComponent implements OnInit {

  readonly roles = [Role.Admin, Role.Host, Role.Coordinator, Role.Teacher];
  readonly HOST_ROLE = Role.Host;
  readonly COORDINATOR_ROLE = Role.Coordinator;
  readonly STUDENT_ROLE = Role.Student;
  readonly TEACHER_ROLE = Role.Teacher;

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;
  @ViewChild('searchUserEmail', {static: false}) private searchUserEmail?: ItemAutocompleteComponent<User>;

  emailField = new FormControl('', [
    Validators.email, Validators.required, Validators.minLength(5), Validators.maxLength(128)
  ]);
  firstNameField = new FormControl('', [
    Validators.minLength(2), Validators.maxLength(128)
  ]);
  lastNameField = new FormControl('', [
    Validators.minLength(2), Validators.maxLength(128)
  ]);
  localeField = new FormControl('', [
    Validators.required
  ]);
  roleField = new FormControl('', [
    Validators.required
  ]);
  inviteForm = new FormGroup({
    email: this.emailField,
    firstName: this.firstNameField,
    lastName: this.lastNameField,
    role: this.roleField,
    locale: this.localeField
  });

  searchModeAllowed = false;
  searchMode = false;
  usernameModeAllowed = false;
  usernameMode = false;

  addMore = true;

  userRoles: Role[] = [];
  callerRoles: Role[] = [];
  requestedRole?: Role;
  readonly locales: Locale[];

  user?: User;
  userNotFound?: boolean;
  userLoading?: boolean;

  private readonly competitionId?: string;
  readonly contest?: Contest;
  grade?: SchoolGrade;
  region?: Region;
  school?: School;
  readonly schoolType?: SchoolType;

  allowGradeSelect = false;
  allowRegionSelect = false;
  allowSchoolTypeSelect = false;
  allowSchoolSelect = false;
  showGradeSelect = false;
  showRegionSelect = false;
  showSchoolSelect = false;

  paymentAllowed = true;
  freeEnrolAllowed = false;

  ctaActions?: MultiActionItem[];

  private tenant?: Competition | null;

  searchUsers = (value: string): Observable<User[]> => {
    return this.userService.searchUsers(value);
  };

  constructor(private competitionService: CompetitionService,
              private contestService: ContestService,
              private tenantService: TenantService,
              private userService: UserService,
              private trackingService: TrackingService,
              private dialog: MatDialog,
              dialogRef: MatDialogRef<InviteUserModalComponent>,
              progressService: ProgressService,
              @Inject(MAT_DIALOG_DATA) data: InviteUserDetailsData) {
    super(dialogRef, progressService);

    if (data) {
      this.competitionId = data.competitionId;
      this.contest = data.contest;

      this.requestedRole = data.requestedRole;

      this.grade = data.grade;
      this.schoolType = data.schoolType;
      this.school = data.school;
      this.region = data.region;

      this.usernameModeChanged(data.usernameMode != undefined ? data.usernameMode : false);
    }

    this.locales = ALL_LOCALES;
  }

  ngOnInit(): void {
    this.startProgress();
    this.localeField.setValue(DEFAULT_LOCALE);

    this.tenant = this.tenantService.getTenant();

    this.firstNameField.disable();
    this.lastNameField.disable();

    this.usernameModeChanged(this.usernameMode);
  }

  compareRoles(r1: Role, r2: Role) {
    return r1 == r2;
  }

  displayRole(role: Role) {
    return localizeRole(role);
  }

  compareLocales(l1: Locale, l2: Locale) {
    return l1 == l2;
  }

  displayLocale(locale: Locale) {
    return localizeLocale(locale);
  }

  displayUser(user?: User): string {
    return user
      ? user.firstName || user.lastName
        ? `${user.firstName} ${user.lastName} (${user.email})`
        : user.email
      : '';
  }

  userSelected(user: User) {
    this.user = user;

    this.searchUserEmail?.disable();

    this.firstNameField.setValue('');
    this.firstNameField.disable();
    this.lastNameField.setValue('');
    this.lastNameField.disable();
  }

  searchModeChanged() {
    this.removeSelectedUser();

    this.searchMode = !this.searchMode;
  }

  usernameModeChanged(mode?: boolean) {
    this.removeSelectedUser();

    this.usernameMode = mode != undefined ? mode : !this.usernameMode;

    if (this.usernameMode) {
      this.user = undefined;
      this.emailField.disable();
      this.firstNameField.enable();
      this.lastNameField.enable();
    } else {
      this.emailField.enable();
      this.firstNameField.disable();
      this.lastNameField.disable();
    }
  }

  proceedAllowed(): boolean {
    return this.competitionId != undefined
      && (this.firstNameField.disabled || this.firstNameField.valid) && (this.lastNameField.disabled || this.lastNameField.valid)
      && ((this.searchMode && this.user != undefined)
        || (!this.searchMode
          && ((this.emailField.enabled && this.emailField.valid && this.userNotFound != undefined)
            || (this.emailField.disabled
              && (this.userNotFound || (this.user != undefined && (!this.school || this.user.schoolId == this.school.id)))))
          && this.localeField.valid && !this.userLoading
          && (!this.allowGradeSelect || this.grade != undefined)
          && (!this.allowRegionSelect || this.region != undefined)
          && (!this.allowSchoolSelect || this.school != undefined)
        )
        || (this.usernameMode && this.user == undefined
          && this.localeField.valid && !this.userLoading
          && (!this.allowGradeSelect || this.grade != undefined)
          && (!this.allowRegionSelect || this.region != undefined)
          && (!this.allowSchoolSelect || this.school != undefined)
        )
      );
  }

  updateCtaActions() {
    const actions: MultiActionItem[] = [];

    if (this.paymentAllowed && this.contest && this.contest.price && this.contest.price > 0) {
      if (this.freeEnrolAllowed) {
        actions.push({
          label: $localize`:General|Free Enrol action label@@general-free-enrol-action-label:Free Enrol`,
          action: 'free-enrol',
          icon: 'how_to_reg'
        });
      }

      if (this.userRoles.includes(Role.Student)) {
        actions.push({
          label: $localize`:General|Enrol and pay action label@@general-exempt-enrol-and-pay-action-label:Enrol & Pay`,
          action: 'enrol-and-pay',
          icon: 'credit_card'
        });
      }
    }

    this.ctaActions = actions;
  }

  ctaActionSelected(action: string) {
    switch (action) {
      case 'exempt-enrol':
        this.exemptionEnrol();
        break;
      case 'free-enrol':
        this.proceed(false, true);
        break;
      case 'enrol-and-pay':
        this.proceed(false);
        break;
    }
  }

  removeSelectedUser() {
    this.user = undefined;
    this.userLoading = undefined;
    this.userNotFound = undefined;

    if (this.searchMode) {
      this.searchUserEmail?.clearInput(true);
      this.searchUserEmail?.enable();
    } else {
      this.emailField.enable();
      this.emailField.setValue('');
      this.emailField.markAsUntouched();
    }

    this.firstNameField.setValue('');
    this.firstNameField.markAsUntouched();
    this.firstNameField.disable();
    this.lastNameField.setValue('');
    this.lastNameField.markAsUntouched();
    this.lastNameField.disable();

    this.grade = undefined;
    this.callerRolesUpdated(this.callerRoles);
  }

  callerRolesUpdated(roles: Role[]) {
    this.callerRoles = roles;

    if (roles.includes(Role.Admin)) {
      this.searchMode = true;
      this.searchModeAllowed = true;
      this.paymentAllowed = false;
      this.userRoles = [Role.Host];
    } else if (roles.includes(Role.Host) || roles.includes(Role.Coordinator)) {
      this.userRoles = [Role.Teacher, Role.Student];

      this.usernameModeAllowed = this.usernameModeCanBeUsed;

      if (roles.includes(Role.Host)) {
        this.userRoles.push(Role.Coordinator);
        this.allowRegionSelect = true;
      }

      if (this.requestedRole && this.userRoles.includes(this.requestedRole)) {
        this.userRoles = [this.requestedRole];
      } else {
        this.userRoles = [];
      }

      if (this.userRoles.includes(Role.Student) &&
        (roles.includes(Role.Host) || !this.contest?.paymentRequired)) {
        this.freeEnrolAllowed = true;
      }

      this.showRegionSelect = true;
      if (this.userRoles.includes(Role.Teacher)) {
        this.allowSchoolTypeSelect = true;
        this.allowSchoolSelect = true;
        this.showSchoolSelect = true;
      } else if (this.userRoles.includes(Role.Student)) {
        this.allowGradeSelect = true;
        this.showGradeSelect = true;
        this.allowSchoolTypeSelect = true;
        this.allowSchoolSelect = true;
        this.showSchoolSelect = true;
      }
    } else if (roles.includes(Role.Teacher)) {
      this.userRoles = [Role.Student];
      this.allowGradeSelect = true;
      this.showGradeSelect = true;
      this.showSchoolSelect = true;

      if (this.userRoles.includes(Role.Student) && !this.contest?.paymentRequired) {
        this.freeEnrolAllowed = true;
      }

      this.usernameModeAllowed = this.usernameModeCanBeUsed;
    }

    this.roleField.setValue(this.userRoles[0]);
    if (this.userRoles.length == 1) {
      this.roleField.disable();
    }

    this.updateCtaActions();
    this.stopProgress();
  }

  get usernameModeCanBeUsed(): boolean {
    return this.tenant?.domain == 'movoznavets'
      && this.userRoles.includes(Role.Student)
      && (!this.requestedRole || this.requestedRole == Role.Student);
  }

  verifyUserExists() {
    if (this.emailField.value && this.emailField.valid) {
      this.user = undefined;
      this.userLoading = true;
      this.userNotFound = undefined;
      this.userService.userExistsAuth(this.emailField.value as string).subscribe(result => {
        if (result.id) {
          this.emailField.disable();

          this.firstNameField.setValue(result.first_name);
          this.firstNameField.disable();
          this.lastNameField.setValue(result.last_name);
          this.lastNameField.disable();
          this.grade = result.school_grade;
          this.allowGradeSelect = false;
          if (this.school) {
            this.allowRegionSelect = false;
            this.allowSchoolTypeSelect = false;
            this.allowSchoolSelect = false;
          }

          const user = User.of(result.id);
          user.email = this.emailField.value as string;
          user.schoolId = result.school_id;
          user.schoolGrade = result.school_grade;
          this.user = user;
        } else {
          this.userNotFound = true;
          this.emailField.disable();
          this.firstNameField.enable();
          this.lastNameField.enable();
        }
        this.userLoading = false;
      });
    }
  }

  gradeSelected(grade: SchoolGrade) {
    this.grade = grade;
  }

  regionSelected(region: Region) {
    this.region = region;
  }

  schoolSelected(data: SchoolSelectResult) {
    this.school = data.school;
  }

  proceed(payLater = true, freeEnrol = false): void {
    this.startProgress();

    if ((!this.searchMode && this.userNotFound) || (this.usernameModeCanBeUsed && this.usernameMode)) {
      const data: InviteUserDto = {
        email: !this.usernameMode ? (this.emailField.value as string || '').trim() : undefined,
        role: this.roleField.value as Role,
        first_name: this.firstNameField.value as string || '',
        last_name: this.lastNameField.value as string || '',
        locale: this.localeField.value as Locale,
        competition_id: this.competitionId,
        region: this.region ? this.region.id : undefined,
        school_id: this.school ? this.school.id : undefined,
        school_grade: this.grade
      };

      this.userService.inviteUser(data).subscribe({
        next: user => {
          this.trackingService.sendEvent(EventType.user_invited, EventCategory.engagement, data.role);

          this.enrolUser(user, payLater, freeEnrol);
        }, error: error => {
          this.stopProgress();

          NotificationHandler.handleError($localize`:Notifications|User invite has failed message@@notifications-user-invite-failed-error:User invite has failed`, error);
        }
      });
    } else if (this.user) {
      this.enrolUser(this.user, payLater, freeEnrol);
    } else {
      this.stopProgress();
    }
  }

  private enrolUser(user: User, payLater = true, freeEnrol = false) {
    if (this.userRoles.includes(Role.Host)) {
      this.competitionService.assignHost(this.competitionId || '', user).subscribe({
        next: () => {
          this.stopProgress();

          const data: InviteUserResult = {
            competitionId: this.competitionId || '',
            user: user,
            exemptEnrol: false,
            freeEnrol: true
          };
          if (this.addMore) {
            data.addMore = this.addMore;
            data.addMoreData = {
              contest: this.contest,
              competitionId: this.competitionId || '',
              requestedRole: this.requestedRole,

              usernameMode: this.usernameModeAllowed ? this.usernameMode : false
            };
          }
          this.closeModal(data);
        }, error: error => {
          this.stopProgress();

          NotificationHandler.handleError($localize`:Notifications|Competition host assign has failed message@@notifications-competition-host-assign-failed-error:Assigning host to a competition has failed`, error);
        }
      });
    } else if (this.contest) {
      const region = this.region ? this.region.id : undefined;
      const school: SchoolBaseDto | undefined = this.school
        ? {id: this.school.id, name: this.school.name, short_name: this.school.short_name}
        : undefined;
      this.contestService.enrol(this.contest.id, user.id, this.roleField.value as Role, region, school, payLater, freeEnrol).subscribe({
        next: u => {
          this.stopProgress();

          if (u.paymentNotConfigured) {
            NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, false);
          }

          const data: InviteUserResult = {
            competitionId: this.competitionId || '',
            user: u,
            paymentPageUrl: u.paymentPageUrl || '',
            freeEnrol: !u.paymentPageUrl || freeEnrol,
            exemptEnrol: false
          };
          if (this.addMore) {
            data.addMore = this.addMore;
            data.addMoreData = {
              contest: this.contest,
              competitionId: this.competitionId || '',

              requestedRole: this.requestedRole,

              grade: this.grade,
              region: this.userRoles[0] != this.COORDINATOR_ROLE ? this.region : undefined,
              school:this.userRoles[0] != this.COORDINATOR_ROLE ? this.school : undefined,
              schoolType: this.userRoles[0] != this.COORDINATOR_ROLE ? this.schoolType : undefined,

              usernameMode: this.usernameModeAllowed ? this.usernameMode : false
            };
          }

          this.closeModal(data);
        }, error: error => {
          this.stopProgress();

          const code = ErrorInterceptor.getStatusCode(error);

          if (code == 412) {
            NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, error, false);
          } else {
            NotificationHandler.handleError($localize`:Notifications|Contest user enrollment has failed message@@notifications-contest-user-enrollment-failed-error:Contest user enrollment has failed`, error);
          }
        }
      });
    } else {
      this.stopProgress();
    }
  }

  exemptionEnrol() {
    const data: InviteUserResult = {
      competitionId: this.competitionId || '',
      exemptEnrol: true
    };

    this.closeModal(data);
  }

  showSchoolDetails(schoolId: string) {
    if (schoolId) {
      const data: SchoolDetailsData = {
        schoolId: schoolId
      };
      this.dialog.open(SchoolDetailsModalComponent, {
        width: '600px',
        data: data
      });
    }
  }
}
